import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import {map, Subject, switchMap, withLatestFrom} from 'rxjs';
import { County } from '../core/classes/county';
import { Territory } from '../core/classes/territory';
import { GenIceCountySummary } from '../generated/serverModels/GenIceCountySummary';
import { GenIceSector } from '../generated/serverModels/GenIceSector';
import { selectCounties } from '../state/location/location.reducer';
import { PopoverData } from '../shared/classes/popover-data';

export type IceStyleProperty = 'costPerCustomerEvent' | 'totalCost';

export interface IceLayerFeature {
  type: 'Feature';
  geometry: ReturnType<typeof JSON.parse>;
  properties: GenIceCountySummary & {
    county: string;
    stateAbbreviation: string;
    tastiDetails?: GenIceCountySummary;
    ntlDetails?: GenIceCountySummary;
    popoverData: PopoverData[];
  };
}

export interface IceLayerScale {
  min: number;
  max: number;
  toValue: (value: number) => number;
}


export interface IceLayerResponse {
  scale: IceLayerScale
  features: IceLayerFeature[];
}

@Injectable({
  providedIn: 'root',
})
export class IceService {

  private http = inject(HttpClient);
  private store = inject(Store);

  private countyCostRequest$ = new Subject<{
    years: number[];
    states: Territory[];
    sector: GenIceSector;
    topCounties?: boolean;
    comparisonProperty: IceStyleProperty;
  }>();


  constructor() {
  }

  public getCountyCostData(
    years: number[],
    states: Territory[],
    sector: GenIceSector,
    topCounties?: boolean,
    comparisonProperty: IceStyleProperty = 'totalCost',
  ): void {
    this.countyCostRequest$.next({ years, states, sector, topCounties, comparisonProperty });
  }


  public countyCostData$ = this.countyCostRequest$.pipe(
    switchMap(({ years, states, sector, topCounties, comparisonProperty }) => {
      const url = 'api/ice/county-summary';
      let params = new HttpParams()
        .set('year', years.join(','))
        .set('sector', sector.toString(),
        );

      params = params.set('state', states.map(s => s.abbreviation).join(','));

      if (topCounties !== undefined) {
        params = params.set('topCounties', String(topCounties));
      }

      return this.http.get<[]>(url, { params }).pipe(
        map((response: GenIceCountySummary[]) => response.map(summary => new GenIceCountySummary(summary))),
        withLatestFrom(this.store.select(selectCounties)),
        map(([summaries, counties]: [GenIceCountySummary[], County[]]): IceLayerResponse => {
          const stateIds = states.map(s => s.id);
          const countiesInState = counties.filter(county => stateIds.includes(county.stateId));

          const features = countiesInState
            .map((county): IceLayerFeature | undefined => {
              const countySummaries = summaries.filter(summary => summary.fipsCode === county.geoid);
              if (countySummaries.length === 0) {
                return undefined;
              }

              const tastiSummary = countySummaries.find(summary => summary.source?.name === 'TASTIGRID');
              const ntlSummary = countySummaries.find(summary => summary.source?.name === 'NTL');
              const first = countySummaries[0];

              return {
                type: 'Feature',
                geometry: JSON.parse(county.boundary),
                properties: {
                  ...first,
                  county: county.name,
                  name: county.name,
                  stateAbbreviation: county.stateAbbreviation,
                  tastiDetails: tastiSummary,
                  ntlDetails: ntlSummary,
                  popoverData: [],
                },
              };
            })
            .filter((feature): feature is IceLayerFeature => feature !== undefined);

          const values = features.map(feature => feature.properties.tastiDetails?.[comparisonProperty]).filter((value): value is number => value !== undefined);

          return {
            scale: IceService.getNormalizedScale(values),
            features: features,
          };

        }),
      );
    }));


  //defined as static since it cant be injected into sources
  static getPopoverForFeature(feature: IceLayerFeature): PopoverData[] {
    const popoverData: PopoverData[] = [];
    const formatCurrency = (value?: number): string => {
      if (value === undefined || value === null) {
        return 'Not Available';
      }

      return '$' + new Intl.NumberFormat('en-US', {  notation: 'compact' }).format(value);
    };
    const definitionMap = {
      numberOfEvents: 'Total number of events affecting customers',
      totalCost: 'The aggregate estimated direct interruption cost for all affected customers in a county.',
      costPerEvent: 'The average estimated interruption cost experienced by an affected customer during a single outage event.',
      costPerCustomerEvent: 'The total modeled outage cost normalized by the customer base, allowing comparison across counties or regions with different customer counts.',
    };

    popoverData.push(new PopoverData({ displayText: 'State', displayValue: feature.properties.stateAbbreviation }));
    popoverData.push(new PopoverData({ displayText: 'County', displayValue: feature.properties.county }));
    popoverData.push(new PopoverData({ displayText: 'Sector', displayValue: feature.properties.sector.name }));
    popoverData.push(new PopoverData({ displayText: '\u00A0', displayValue: '\u00A0' }));

    const tasti = feature.properties.tastiDetails;
    const ntl = feature.properties.ntlDetails;

    if (tasti) {
      popoverData.push(new PopoverData({ displayText: 'Source', displayValue: 'TASTI-GRID' }));
      popoverData.push(new PopoverData({ displayText: 'Years', displayValue: tasti.yearList.toString() }));
      popoverData.push(new PopoverData({ displayText: 'Number of Events', displayValue: tasti.eventCount, infoText: definitionMap.numberOfEvents }));
      popoverData.push(new PopoverData({ displayText: 'Total Cost', displayValue: formatCurrency(tasti.totalCost), infoText: definitionMap.totalCost }));
      popoverData.push(new PopoverData({ displayText: 'Cost Per Event', displayValue: formatCurrency(tasti.costPerEvent), infoText: definitionMap.costPerEvent }));
      popoverData.push(new PopoverData({ displayText: 'Cost Per Customer', displayValue: formatCurrency(tasti.costPerCustomerEvent), infoText: definitionMap.costPerCustomerEvent }));
    }

    if (tasti && ntl) {
      popoverData.push(new PopoverData({ displayText: '\u00A0', displayValue: '\u00A0' }));
    }

    if (ntl) {
      popoverData.push(new PopoverData({ displayText: 'Source', displayValue: 'Night Time Lights' }));
      popoverData.push(new PopoverData({ displayText: 'Years', displayValue: ntl.yearList.toString() }));
      popoverData.push(new PopoverData({ displayText: 'Number of Events', displayValue: ntl.eventCount, infoText: definitionMap.numberOfEvents }));
      popoverData.push(new PopoverData({ displayText: 'Total Cost', displayValue: formatCurrency(ntl.totalCost), infoText: definitionMap.totalCost }));
      popoverData.push(new PopoverData({ displayText: 'Cost Per Event', displayValue: formatCurrency(ntl.costPerEvent), infoText: definitionMap.costPerEvent }));
      popoverData.push(new PopoverData({ displayText: 'Cost Per Customer', displayValue: formatCurrency(ntl.costPerCustomerEvent), infoText: definitionMap.costPerCustomerEvent }));
    }

    return popoverData;
  }

  static getNormalizedScale(values: number[]) {
    if (values.length === 0) {
      return {
        min: 0,
        max: 0,
        toValue: () => 0,
      };
    }

    const valuesSorted = [...values].sort((a, b) => a - b);
    const min = valuesSorted[0];
    const max = valuesSorted[valuesSorted.length - 1];

    // Detect outliers using IQR method
    const q1Index = Math.floor(valuesSorted.length * 0.25);
    const q3Index = Math.floor(valuesSorted.length * 0.75);
    const q1 = valuesSorted[q1Index];
    const q3 = valuesSorted[q3Index];
    const iqr = q3 - q1;
    const spread = max - min;
    const denominator = iqr !== 0 ? iqr : spread;
    const baseline = iqr !== 0 ? q1 : min;

    return {
      min,
      max,
      toValue: (value: number) => {
        if (denominator === 0) {
          return 0;
        }

        return (value - baseline) / denominator;
      },
    };
  }
}
